import React, { useMemo, useState } from "react";
import "../../../css/Finance/SettlementRelatedTransfersSection.css";

const TRIP_INCOME_CATEGORY_ID = 1;
const TRIP_EXPENSE_CATEGORY_ID = 2;
const TRIP_ADVANCE_REFUND_CATEGORY_ID = 3;
const TRIP_EXPENSE_REFUND_CATEGORY_ID = 5;
const CURRENCY_PURCHASE_CATEGORY_ID = 24;

const SettlementRelatedTransfersSection = ({ transfers = [] }) => {
    const [activeTab, setActiveTab] = useState("expenses");
    const [isCollapsed, setIsCollapsed] = useState(false);

    const expenseTransfers = useMemo(
        () => transfers.filter((transfer) =>
            transfer.categoryId === TRIP_EXPENSE_CATEGORY_ID ||
            transfer.categoryId === TRIP_EXPENSE_REFUND_CATEGORY_ID ||
            transfer.categoryId === CURRENCY_PURCHASE_CATEGORY_ID
        ),
        [transfers]
    );

    const incomeTransfers = useMemo(
        () => transfers.filter((transfer) => transfer.categoryId === TRIP_INCOME_CATEGORY_ID),
        [transfers]
    );

    const refundTransfers = useMemo(
        () => transfers.filter((transfer) => transfer.categoryId === TRIP_ADVANCE_REFUND_CATEGORY_ID),
        [transfers]
    );

    const activeTransfers = useMemo(() => {
        switch (activeTab) {
            case "income": return incomeTransfers;
            case "refunds": return refundTransfers;
            default: return expenseTransfers;
        }
    }, [activeTab, expenseTransfers, incomeTransfers, refundTransfers]);

    const totalAmount = useMemo(
        () => activeTransfers.reduce((sum, transfer) => sum + Number(transfer.amount ?? 0), 0),
        [activeTransfers]
    );

    const formatAmount = (amount) => new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format(amount);
    const formatDate = (date) => date ? new Date(date).toLocaleDateString("pl-PL") : "—";

    const getTransferLabel = (count) => {
        if (count === 1) return "przelew";
        if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14)) return "przelewy";
        return "przelewów";
    };

    const getAmountClass = (amount) => Number(amount) >= 0 ? "settlement-related-transfer-amount-positive" : "settlement-related-transfer-amount-negative";
    const getTotalClass = () => totalAmount >= 0 ? "settlement-related-transfers-total-positive" : "settlement-related-transfers-total-negative";

    return (
        <div className={`settlement-related-transfers-section finance-section-card ${isCollapsed ? "is-collapsed" : ""}`}>
            <div className="settlement-related-transfers-header">
                <div className="settlement-related-transfers-header-top" onClick={() => setIsCollapsed((prev) => !prev)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setIsCollapsed((prev) => !prev); }}>
                    <div className="settlement-related-transfers-header-left">
                        <div className="settlement-related-transfers-header-icon"><i className="fa-solid fa-money-bill-transfer" /></div>
                        <div className="settlement-related-transfers-header-content">
                            <h3>Przelewy powiązane z wycieczką</h3>
                            <p className="settlement-related-transfers-subtitle">Koszty, wpływy i zwroty związane z tą wycieczką.</p>
                        </div>
                    </div>

                    <div className="settlement-related-transfers-header-right">
                        {activeTransfers.length > 0 && (
                            <div className="settlement-related-transfers-summary">
                                <div className="settlement-related-transfers-count">{activeTransfers.length} {getTransferLabel(activeTransfers.length)}</div>
                                <div className={`settlement-related-transfers-total ${getTotalClass()}`}>{formatAmount(totalAmount)}</div>
                            </div>
                        )}
                        <div className="settlement-related-transfers-collapse-icon" title={isCollapsed ? "Rozwiń" : "Zwiń"}><i className={`fa-solid fa-chevron-${isCollapsed ? "down" : "up"}`} /></div>
                    </div>
                </div>

                {!isCollapsed && (
                    <div className="settlement-related-transfers-tabs">
                        <div className={`settlement-related-transfers-tab ${activeTab === "expenses" ? "active" : ""}`} onClick={() => setActiveTab("expenses")}>Koszty wycieczki<span>{expenseTransfers.length}</span></div>
                        <div className={`settlement-related-transfers-tab ${activeTab === "income" ? "active" : ""}`} onClick={() => setActiveTab("income")}>Wpływy<span>{incomeTransfers.length}</span></div>
                        <div className={`settlement-related-transfers-tab ${activeTab === "refunds" ? "active" : ""}`} onClick={() => setActiveTab("refunds")}>Zwroty<span>{refundTransfers.length}</span></div>
                    </div>
                )}
            </div>

            {!isCollapsed && (activeTransfers.length === 0 ? (
                <div className="settlement-related-transfers-empty">
                    <div className="settlement-related-transfers-empty-icon"><i className="fa-solid fa-circle-info" /></div>
                    <div>Brak przelewów w tej kategorii.</div>
                </div>
            ) : (
                <div className="settlement-related-transfers-list">
                    {activeTransfers.map((transfer) => (
                        <div key={transfer.guid} className="settlement-related-transfer-row">
                            <div className="settlement-related-transfer-date"><i className="fa-regular fa-calendar" /><span>{formatDate(transfer.transferDate)}</span></div>
                            <div className="settlement-related-transfer-content">
                                <div className="settlement-related-transfer-title">{transfer.title || "Brak tytułu"}</div>
                                {transfer.senderName && <div className="settlement-related-transfer-contractor"><i className="fa-regular fa-user" /><span>{transfer.senderName}</span></div>}
                            </div>
                            <div className={`settlement-related-transfer-amount ${getAmountClass(transfer.amount)}`}>{formatAmount(transfer.amount)}</div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default SettlementRelatedTransfersSection;